import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Field,
  Modal,
  ModalActions,
  Table,
  cn,
  type Action,
} from '@evogard/ui';
import type { ContaazulAccount } from '@evogard/contract';
import {
  count,
  formatDocument,
  isValidCnpj,
  jwtProblem,
  opaqueSecretProblem,
} from '@evogard/domain';
import { api, ok, errorMessage } from '../../lib/api.js';
import { ContaazulAccessPanel } from './ContaazulAccessPanel.js';

export function contaazulKey(tenantId: number | undefined) {
  return ['plataforma', 'integracoes', 'contaazul', tenantId];
}

/** As contas desta base — a tela de integrações lê a mesma para contar na lista. */
export function useContaazulAccounts(tenantId: number | undefined) {
  return useQuery({
    // a base na chave: trocar de praça troca as contas que estão na tela
    queryKey: contaazulKey(tenantId),
    queryFn: async () => {
      const r = await api.contaazul.listContaazulAccounts();
      if (r.status !== 200) {
        throw new Error(errorMessage(r.body, 'Não foi possível carregar as contas.'));
      }
      return r.body.accounts;
    },
    enabled: tenantId !== undefined,
  });
}

/**
 * O card do Conta Azul na tela de integrações.
 *
 * Diferente dos outros cards: a praça pode ter VÁRIAS contas no Conta Azul
 * (uma por empresa — "Evogard Comercial"), e cada uma tem o seu par
 * `client_id` + `client_secret`. Por isso aqui não há o campo único de colar a
 * credencial: há uma tabela de contas, no desenho de cadastro da casa
 * (`CompaniesScreen`) — "Nova conta" no cabeçalho do cartão, e editar e
 * arquivar nas reticências da linha, com o formulário dentro do próprio painel
 * de ações (nunca um modal abrindo outro).
 *
 * **Testar conexão** lê a empresa que o access token alcança no Conta Azul e
 * confere o CNPJ. Sempre no clique: salvar o token não testa, e a tela não
 * testa sozinha ao abrir (decidido em 01/10/2026). É a única chamada ao Conta Azul, e só lê: o refresh token não
 * entra na plataforma enquanto o legado renovar a mesma cadeia.
 *
 * O segredo segue a régua da tela inteira: nunca volta. Na edição o campo
 * começa vazio, e vazio quer dizer "manter o que está guardado".
 */
export function ContaazulAccountsCard({
  label,
  tenantId,
  canEdit,
  canTest,
}: {
  label: string;
  tenantId: number | undefined;
  canEdit: boolean;
  canTest: boolean;
}) {
  const client = useQueryClient();
  const key = contaazulKey(tenantId);
  const [creating, setCreating] = useState(false);
  /** A conta cuja autorização de uso está aberta no diálogo. */
  const [accessFor, setAccessFor] = useState<ContaazulAccount | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const query = useContaazulAccounts(tenantId);

  const refresh = () => client.invalidateQueries({ queryKey: key });

  const archive = useMutation({
    mutationFn: async (id: number) => {
      const r = await api.contaazul.updateContaazulAccount({
        params: { id },
        body: { archived: true },
      });
      return ok(r, 200, 'Não foi possível arquivar a conta.');
    },
    onSuccess: async (account) => {
      await refresh();
      setNote(`${account.name} foi arquivada, e o client secret dela foi apagado.`);
    },
  });

  const test = useMutation({
    mutationFn: async (id: number) => {
      const r = await api.contaazul.testContaazulAccount({ params: { id }, body: {} });
      return ok(r, 200, 'Não foi possível testar a conta.');
    },
    onSuccess: async (body) => {
      await refresh();
      // a falha do teste é resposta do Conta Azul, e vai como texto — não como erro nosso
      if (body.result.ok) {
        setActionError(null);
        setNote(body.result.detail);
      } else {
        setNote(null);
        setActionError(body.result.detail);
      }
    },
    onError: (e: Error) => {
      setNote(null);
      setActionError(e.message);
    },
  });

  const accounts = query.data;

  return (
    <Card
      title={label}
      descricao={
        accounts === undefined
          ? undefined
          : `${count(accounts.length, 'conta', 'contas')} nesta base.`
      }
      actions={
        /* Esconder é cortesia; a rota continua exigindo `platform.integrations:editar`. */
        canEdit && (
          <Button icon="mais" size="sm" onClick={() => setCreating(true)}>
            Nova conta
          </Button>
        )
      }
      bleed
    >
      {(query.isError || note || actionError) && (
        <div className="flex flex-col gap-2 px-5 pb-4">
          {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
          {actionError && <Alert tone="erro">{actionError}</Alert>}
          {note && <Alert tone="sucesso">{note}</Alert>}
        </div>
      )}

      <Table
        paginate={false}
        label="Contas do Conta Azul desta base"
        items={accounts ?? []}
        loading={query.isLoading}
        skeletonRows={2}
        rowKey={(a) => String(a.id)}
        rowTitle={(a) => a.name}
        rowSubtitle={(a) => `CNPJ ${formatDocument(a.document)}`}
        rowIcon="chave"
        empty="Nenhuma conta do Conta Azul nesta base. A primeira entra por Nova conta."
        rowActions={(a): Action[] => [
          ...(canTest
            ? ([
                {
                  label: test.isPending ? 'Testando…' : 'Testar conexão',
                  icon: 'atualizar' as const,
                  disabled: !a.hasAccessToken
                    ? 'A conta não tem access token. Cole um em Editar antes de testar.'
                    : (false as const),
                  run: () => {
                    setNote(null);
                    setActionError(null);
                    test.mutate(a.id);
                  },
                },
              ] satisfies Action[])
            : []),
          ...(!canEdit
            ? []
            : ([
                {
                  label: 'Autorização',
                  icon: 'pessoas',
                  run: () => setAccessFor(a),
                },
                {
                  label: 'Editar',
                  icon: 'etiqueta',
                  form: (close) => (
                    <AccountForm
                      editing={a}
                      onSaved={async (saved) => {
                        await refresh();
                        setActionError(null);
                        setNote(`${saved.name} foi salva.`);
                        close();
                      }}
                      onCancel={close}
                    />
                  ),
                },
                {
                  label: 'Arquivar',
                  icon: 'bloqueado',
                  tone: 'perigo',
                  form: (close) => (
                    <div className="flex flex-col gap-4">
                      <p className="text-sm text-texto-suave">
                        <strong className="font-medium text-texto">{a.name}</strong> sai
                        da lista e o client secret é apagado. Não dá para desfazer.
                      </p>
                      <ModalActions>
                        <Button type="button" variant="contorno" onClick={close}>
                          Cancelar
                        </Button>
                        <Button
                          type="button"
                          variant="perigo"
                          disabled={archive.isPending}
                          onClick={() => {
                            setNote(null);
                            setActionError(null);
                            archive.mutate(a.id, {
                              onSuccess: close,
                              onError: (e: Error) => {
                                setActionError(e.message);
                                close();
                              },
                            });
                          }}
                        >
                          {archive.isPending ? 'Arquivando…' : 'Arquivar'}
                        </Button>
                      </ModalActions>
                    </div>
                  ),
                },
              ] satisfies Action[])),
        ]}
        columns={[
          {
            title: 'Conta',
            alignment: 'inicio' as const,
            minWidth: '13rem',
            cell: (a) => (
              <div className="flex min-w-0 items-center gap-2.5">
                <Avatar nome={a.name} size="md" />
                <p className="truncate font-semibold">{a.name}</p>
              </div>
            ),
          },
          {
            title: 'CNPJ',
            minWidth: '10rem',
            cell: (a) => (
              <span className="tabular-nums">{formatDocument(a.document)}</span>
            ),
          },
          {
            title: 'Client ID',
            // fixa: revelar não pode empurrar as outras colunas
            width: '16rem',
            cell: (a) => <MaskedClientId account={a} />,
          },
          {
            title: 'Client secret',
            minWidth: '9rem',
            cell: (a) =>
              a.hasClientSecret && a.clientSecretUpdatedAt ? (
                <StatusStack
                  badge={<Badge tone="sucesso">Guardado</Badge>}
                  lines={[`desde ${formatDate(a.clientSecretUpdatedAt)}`]}
                />
              ) : (
                <Badge tone="neutro">Sem client secret</Badge>
              ),
          },
          {
            title: 'Conexão',
            minWidth: '12rem',
            cell: (a) => <ConnectionCell account={a} />,
          },
        ]}
      />

      <Modal
        open={accessFor !== null}
        onOpenChange={(open) => !open && setAccessFor(null)}
        title="Quem pode usar esta conta"
        descricao={accessFor?.name}
        size="lg"
      >
        {accessFor && <ContaazulAccessPanel account={accessFor} />}
      </Modal>

      <Modal
        open={creating}
        onOpenChange={setCreating}
        title="Nova conta do Conta Azul"
        descricao="Client id e client secret do app da empresa no Conta Azul."
      >
        <AccountForm
          onSaved={async (saved) => {
            await refresh();
            setActionError(null);
            setNote(`${saved.name} foi cadastrada.`);
            setCreating(false);
          }}
          onCancel={() => setCreating(false)}
        />
      </Modal>
    </Card>
  );
}

/**
 * O formulário da conta — o mesmo para cadastrar (no `Modal`) e para editar
 * (dentro do painel de ações da linha).
 */
function AccountForm({
  editing,
  onSaved,
  onCancel,
}: {
  editing?: ContaazulAccount;
  onSaved: (account: ContaazulAccount) => void | Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(editing?.name ?? '');
  const [document, setDocument] = useState(
    editing ? formatDocument(editing.document) : '',
  );
  const [clientId, setClientId] = useState(editing?.clientId ?? '');
  const [clientSecret, setClientSecret] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  /* os erros dos campos aparecem depois da primeira tentativa de seguir, não a cada tecla */
  const [submitted, setSubmitted] = useState(false);
  const [step, setStep] = useState<'preencher' | 'confirmar'>('preencher');

  /*
   * A mesma regra que o servidor aplica (`credential-format.ts` no domínio, e o
   * contrato): a tela avisa, e é o servidor quem recusa. O vencimento do access
   * token é conferido com o relógio de quem digita — o servidor confere de novo
   * com o dele.
   */
  const problems = {
    name: name.trim().length === 0 ? 'Informe o nome da conta.' : null,
    document:
      document.trim().length === 0
        ? 'Informe o CNPJ.'
        : isValidCnpj(document)
          ? null
          : 'CNPJ inválido: confira os dígitos.',
    clientId: opaqueSecretProblem(clientId, 'o client id'),
    clientSecret:
      editing && clientSecret.trim().length === 0
        ? null
        : opaqueSecretProblem(clientSecret, 'o client secret'),
    accessToken:
      accessToken.trim().length === 0 ? null : jwtProblem(accessToken, new Date()),
  };
  const hasProblem = Object.values(problems).some((p) => p !== null);
  const shown = (p: string | null) => (submitted ? p : null);

  const save = useMutation({
    mutationFn: async () => {
      if (editing) {
        const r = await api.contaazul.updateContaazulAccount({
          params: { id: editing.id },
          body: {
            name,
            document,
            clientId,
            // vazio é "manter o guardado" — o campo nunca vem preenchido
            ...(clientSecret.trim() ? { clientSecret } : {}),
            ...(accessToken.trim() ? { accessToken } : {}),
          },
        });
        return ok(r, 200, 'Não foi possível salvar a conta.');
      }
      const r = await api.contaazul.createContaazulAccount({
        body: {
          name,
          document,
          clientId,
          clientSecret,
          ...(accessToken.trim() ? { accessToken } : {}),
        },
      });
      return ok(r, 201, 'Não foi possível cadastrar a conta.');
    },
    onSuccess: (account) => onSaved(account),
    // o segredo some do campo em qualquer desfecho, como na credencial
    onError: (e: Error) => {
      setClientSecret('');
      setAccessToken('');
      setSubmitted(false);
      setStep('preencher');
      setError(e.message);
    },
  });

  /** O que a confirmação diz sobre cada segredo: o que entra, o que é trocado, o que fica. */
  const secretLine = (label: string, typed: string, stored: boolean) =>
    typed.trim()
      ? stored
        ? `${label}: substituído.`
        : `${label}: salvo.`
      : stored
        ? `${label}: mantido.`
        : `${label}: nenhum.`;

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        if (step === 'preencher') {
          setSubmitted(true);
          if (!hasProblem) setStep('confirmar');
          return;
        }
        save.mutate();
      }}
    >
      {error && <Alert tone="erro">{error}</Alert>}
      {step === 'preencher' ? (
        <>
          <Field
            label="Nome"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={shown(problems.name)}
            help="Ex.: Evogard Comercial."
          />
          <Field
            label="CNPJ"
            mask="cnpj"
            inputMode="numeric"
            value={document}
            onChange={(e) => setDocument(e.target.value)}
            error={shown(problems.document)}
          />
          <Field
            label="Client ID"
            autoComplete="off"
            spellCheck={false}
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            error={shown(problems.clientId)}
          />
          <Field
            label={editing ? 'Novo client secret' : 'Client secret'}
            /*
             * `password` e `off` pelo mesmo motivo do campo de credencial: o
             * navegador ofereceria o segredo de uma conta no formulário da outra.
             */
            type="password"
            autoComplete="off"
            spellCheck={false}
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            error={shown(problems.clientSecret)}
            help={editing ? 'Em branco mantém o salvo.' : undefined}
          />
          <Field
            label={
              editing?.hasAccessToken ? 'Novo access token' : 'Access token (opcional)'
            }
            type="password"
            autoComplete="off"
            spellCheck={false}
            value={accessToken}
            onChange={(e) => setAccessToken(e.target.value)}
            error={shown(problems.accessToken)}
            help={
              editing?.hasAccessToken
                ? 'Em branco mantém o salvo. Vale por 1 hora.'
                : 'Para testar a conexão. Vale por 1 hora.'
            }
          />
        </>
      ) : (
        <div className="flex flex-col gap-2 text-sm text-texto-suave">
          <p>
            {editing ? 'Salvar' : 'Cadastrar'}{' '}
            <strong className="font-medium text-texto">{name.trim()}</strong>, CNPJ{' '}
            <span className="tabular-nums text-texto">{formatDocument(document)}</span>,
            client id <code className="font-mono text-texto">{clientId.trim()}</code>?
          </p>
          <ul className="list-disc pl-5">
            <li>{secretLine('Client secret', clientSecret, Boolean(editing))}</li>
            <li>
              {secretLine('Access token', accessToken, Boolean(editing?.hasAccessToken))}
            </li>
          </ul>
        </div>
      )}
      <ModalActions>
        {step === 'preencher' ? (
          <>
            <Button type="button" variant="contorno" onClick={onCancel}>
              Cancelar
            </Button>
            <Button type="submit">Continuar</Button>
          </>
        ) : (
          <>
            <Button
              type="button"
              variant="contorno"
              disabled={save.isPending}
              onClick={() => setStep('preencher')}
            >
              Voltar
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending
                ? 'Salvando…'
                : editing
                  ? 'Confirmar e salvar'
                  : 'Confirmar e cadastrar'}
            </Button>
          </>
        )}
      </ModalActions>
    </form>
  );
}

/**
 * O Client ID ofuscado na tabela, revelado no clique.
 *
 * **É cortesia contra quem olha por cima do ombro, não proteção.** O Client ID
 * não é segredo — vai no link de autorização do Conta Azul — e a resposta da
 * API o traz inteiro; o que nunca sai da API é o client secret. Por isso o
 * revelar é estado da tela, sem rota nem permissão próprias, e volta a ofuscar
 * quando a lista é desenhada de novo.
 *
 * O ofuscado tem largura fixa: o tamanho do valor também diz algo sobre ele.
 */
function MaskedClientId({ account }: { account: ContaazulAccount }) {
  const [shown, setShown] = useState(false);
  return (
    <span className="inline-flex max-w-full items-center gap-2">
      <span
        className="min-w-0 truncate font-mono text-xs"
        title={shown ? account.clientId : undefined}
      >
        {shown ? account.clientId : '••••••••••••'}
      </span>
      <Button
        type="button"
        variant="discreto"
        size="sm"
        icon="olho"
        aria-label={`${shown ? 'Ocultar' : 'Mostrar'} o client id de ${account.name}`}
        aria-pressed={shown}
        onClick={() => setShown(!shown)}
      >
        {shown ? 'Ocultar' : 'Mostrar'}
      </Button>
    </span>
  );
}

/**
 * O estado da conexão: o último teste, e quando o access token vence.
 *
 * O vencimento é o `exp` do próprio token, lido na gravação — informação, não
 * veredito. Quem diz se o token vale é o Conta Azul, no teste.
 */
function ConnectionCell({ account: a }: { account: ContaazulAccount }) {
  const expired =
    a.accessTokenExpiresAt !== null && new Date(a.accessTokenExpiresAt) < new Date();
  const badge =
    a.verifiedOk === true ? (
      <Badge tone="sucesso">Conectada</Badge>
    ) : a.verifiedOk === false ? (
      <Badge tone="erro">Teste falhou</Badge>
    ) : a.hasAccessToken ? (
      <Badge tone="neutro">Não testada</Badge>
    ) : (
      <Badge tone="neutro">Sem access token</Badge>
    );
  const token = !a.hasAccessToken
    ? null
    : a.accessTokenExpiresAt === null
      ? 'token sem data de vencimento'
      : `token ${expired ? 'venceu' : 'vence'} ${formatDate(a.accessTokenExpiresAt)}`;

  return (
    <StatusStack
      badge={<span title={a.verifiedDetail ?? undefined}>{badge}</span>}
      lines={[a.verifiedOk ? (a.connectedCompany?.name ?? null) : null, token]}
      alarmLast={expired}
    />
  );
}

/**
 * Selo curto em cima, a data numa linha pequena embaixo — para a coluna não
 * alargar a tabela além do cartão com um selo de duas linhas.
 */
function StatusStack({
  badge,
  lines,
  alarmLast = false,
}: {
  badge: ReactNode;
  lines: (string | null)[];
  /** a última linha em vermelho — o token que já venceu */
  alarmLast?: boolean;
}) {
  return (
    <span className="inline-flex flex-col items-center gap-0.5">
      {badge}
      {lines.map((line, i) =>
        line ? (
          <span
            key={i}
            className={cn(
              'whitespace-nowrap text-xs',
              alarmLast && i === lines.length - 1 ? 'text-erro' : 'text-texto-suave',
            )}
          >
            {line}
          </span>
        ) : null,
      )}
    </span>
  );
}

/** Data e hora curtas, como no resto da tela. */
function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(iso));
}
